/**
 * The save indicator in the app bar.
 *
 * State is always visible and never announced. A toast for a thing that worked
 * is noise, and the one piece of information someone actually wants — "is my
 * work safe?" — should be readable at a glance rather than having appeared and
 * vanished three seconds ago.
 *
 * The states are deliberately about *where the work is*, not about what the
 * network did. "Offline, saved on this device" is reassuring; "Network error"
 * is the same fact told in a way that makes people stop working.
 */
import { el } from "./dom";

export type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; at: number }
  | { kind: "local"; reason: "offline" | "signed-out" }
  | { kind: "read-only" }
  | { kind: "conflict" }
  | { kind: "error"; message: string };

export function describeSaveState(state: SaveState): { text: string; tone: string } {
  switch (state.kind) {
    case "idle":
      return { text: "", tone: "idle" };
    case "saving":
      return { text: "Saving…", tone: "busy" };
    case "saved":
      return { text: "Saved", tone: "ok" };
    case "local":
      return {
        text:
          state.reason === "offline" ? "Offline — saved on this device" : "Saved on this device",
        tone: "local",
      };
    case "read-only":
      // Not an error: the work is intact, the plan simply does not include
      // writing to this one. Saying "read-only" beats "save failed", which
      // sounds like something was lost.
      return { text: "Read-only", tone: "locked" };
    case "conflict":
      return { text: "Edited on another device", tone: "warn" };
    case "error":
      return { text: state.message, tone: "error" };
  }
}

export interface SaveIndicator {
  el: HTMLElement;
  set(state: SaveState): void;
  destroy(): void;
}

export function createSaveIndicator(): SaveIndicator {
  const node = el("span", {
    class: "hud-save",
    aria: { live: "polite", atomic: "true" },
    data: { tone: "idle" },
  });

  let current: SaveState = { kind: "idle" };
  let tick: ReturnType<typeof setInterval> | null = null;

  function render() {
    const { text, tone } = describeSaveState(current);
    node.dataset.tone = tone;
    node.textContent = text;
    node.title =
      current.kind === "saved" ? `Last saved ${new Date(current.at).toLocaleTimeString()}` : "";
  }

  return {
    el: node,
    set(state) {
      current = state;
      render();

      // "Saved" is worth showing, then worth getting out of the way. Fading it
      // to idle after a while keeps the app bar quiet without a toast.
      if (tick) clearInterval(tick);
      tick = null;
      if (state.kind === "saved") {
        tick = setInterval(() => {
          if (current.kind === "saved" && Date.now() - current.at > 8_000) {
            current = { kind: "idle" };
            render();
            if (tick) clearInterval(tick);
            tick = null;
          }
        }, 2_000);
      }
    },
    destroy() {
      if (tick) clearInterval(tick);
      node.remove();
    },
  };
}
